import * as layout from '../markLayout.module.css';
import React, { useCallback, useRef, useState } from 'react';
import P from '@hero/ui-kit/typography/P';
import type { Block, Channel } from '../../types';
import { NESTING_LIMIT, SMS_ALLOWED_BLOCKS } from './constants';
import { useEditorContext } from './EditorContext';
import BlockToolbar from './BlockToolbar';
import BlockInsertMenu from './BlockInsertMenu';
import EditableBlock from './EditableBlock';
import CanvasBlockList from './CanvasBlockList';
type Props = {
    blocks: Block[];
    onChange: (blocks: Block[]) => void;
    channel: Channel;
    depth: number;
    label?: string;
    variant?: 'root' | 'nested';
    showInsertLabels?: boolean;
};
const LegacyBlockList: React.FC<Props> = ({
    blocks,
    onChange,
    channel,
    depth,
    label,
    variant = 'nested',
    showInsertLabels = false
}) => {
    const { setInsertTarget } = useEditorContext();
    const containerRef = useRef<HTMLDivElement>(null);
    const liveRef = useRef<HTMLDivElement>(null);
    const [menuResetKey, setMenuResetKey] = useState(0);
    const isRoot = variant === 'root';
    const announce = useCallback((message: string) => {
        if (liveRef.current) liveRef.current.textContent = message;
    }, []);
    const focusToolbar = useCallback((index: number) => {
        requestAnimationFrame(() => {
            const wrapper = containerRef.current?.querySelector(
                `:scope > [data-block-index="${index}"]`
            );
            const button = wrapper?.querySelector(
                '[data-block-toolbar] button:not(:disabled)'
            ) as HTMLElement | null;
            button?.focus();
        });
    }, []);
    const mutate = useCallback(
        (newBlocks: Block[]) => {
            setInsertTarget(null);
            setMenuResetKey((current) => current + 1);
            onChange(newBlocks);
        },
        [onChange, setInsertTarget]
    );
    if (depth > NESTING_LIMIT) {
        return (
            <P
                noDefaultMargin
                size="small"
                styles={{
                    color: 'var(--mark-muted)',
                    padding: '0.4rem 0'
                }}
            >
                Maximum nesting depth reached.
            </P>
        );
    }
    const handleUpdate = (index: number, updated: Block) => {
        onChange(blocks.map((block, i) => (i === index ? updated : block)));
    };
    const handleDelete = (index: number) => {
        mutate(blocks.filter((_, i) => i !== index));
        if (blocks.length <= 1) {
            requestAnimationFrame(() => {
                const button = containerRef.current?.querySelector('button') as HTMLElement | null;
                button?.focus();
            });
        } else {
            focusToolbar(Math.min(index, blocks.length - 2));
        }
        announce(`Block ${index + 1} deleted.`);
    };
    const handleDuplicate = (index: number) => {
        const copy = JSON.parse(JSON.stringify(blocks[index])) as Block;
        mutate([...blocks.slice(0, index + 1), copy, ...blocks.slice(index + 1)]);
        focusToolbar(index + 1);
        announce(`Block ${index + 1} duplicated.`);
    };
    const handleMoveUp = (index: number) => {
        if (index === 0) return;
        const next = [...blocks];
        [next[index - 1], next[index]] = [next[index], next[index - 1]];
        mutate(next);
        focusToolbar(index - 1);
        announce(`Block moved up to position ${index}.`);
    };
    const handleMoveDown = (index: number) => {
        if (index >= blocks.length - 1) return;
        const next = [...blocks];
        [next[index], next[index + 1]] = [next[index + 1], next[index]];
        mutate(next);
        focusToolbar(index + 1);
        announce(`Block moved down to position ${index + 2}.`);
    };
    const handleInsert = (index: number, block: Block) => {
        mutate([...blocks.slice(0, index), block, ...blocks.slice(index)]);
        focusToolbar(index);
        announce(`Block added at position ${index + 1}.`);
    };
    const parentLabel = isRoot ? undefined : label ? `${label}, depth ${depth}` : `Depth ${depth}`;
    return (
        <div
            className={layout.form}
            ref={containerRef}
            style={
                isRoot
                    ? undefined
                    : { paddingLeft: '0.8rem', borderLeft: '2px solid var(--mark-border)' }
            }
        >
            <div
                ref={liveRef}
                aria-live="polite"
                style={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    overflow: 'hidden',
                    clip: 'rect(0,0,0,0)'
                }}
            />
            {!isRoot && label && (
                <span
                    style={{
                        fontWeight: 600,
                        color: 'var(--mark-muted)',
                        display: 'block',
                        marginBottom: '0.4rem'
                    }}
                >
                    {label}
                </span>
            )}
            <BlockInsertMenu
                key={`start-${menuResetKey}`}
                onInsert={(block) => handleInsert(0, block)}
                channel={channel}
                depth={depth}
                position={1}
                parentLabel={parentLabel}
                showLabel={showInsertLabels}
            />
            {blocks.map((block, index) => {
                const isSmsDisallowed =
                    channel === 'sms' &&
                    !(SMS_ALLOWED_BLOCKS as readonly string[]).includes(block.type);
                return (
                    <div
                        key={`${menuResetKey}-${index}`}
                        data-block-index={index}
                        style={{
                            marginBottom: '0.4rem',
                            backgroundColor: 'var(--mark-surface)'
                        }}
                        className={layout.panel}
                    >
                        {isSmsDisallowed && (
                            <div
                                style={{
                                    backgroundColor: 'var(--mark-warning-surface)',
                                    marginBottom: '0.4rem',
                                    color: 'var(--mark-warning)'
                                }}
                                className={layout.panel}
                            >
                                This block type is not supported in SMS templates.
                            </div>
                        )}
                        <BlockToolbar
                            index={index}
                            total={blocks.length}
                            onMoveUp={() => handleMoveUp(index)}
                            onMoveDown={() => handleMoveDown(index)}
                            onDuplicate={() => handleDuplicate(index)}
                            onDelete={() => handleDelete(index)}
                            duplicateDisabledReason={
                                isSmsDisallowed
                                    ? 'This block type is not supported in SMS templates'
                                    : undefined
                            }
                            parentLabel={parentLabel}
                        />
                        <fieldset
                            disabled={isSmsDisallowed}
                            style={{
                                margin: 0,
                                opacity: isSmsDisallowed ? 0.5 : 1
                            }}
                            className={layout.fieldset}
                        >
                            <EditableBlock
                                block={block}
                                onChange={(updated) => handleUpdate(index, updated)}
                                depth={depth}
                            />
                        </fieldset>
                        <BlockInsertMenu
                            key={`after-${index}-${menuResetKey}`}
                            onInsert={(newBlock) => handleInsert(index + 1, newBlock)}
                            channel={channel}
                            depth={depth}
                            position={index + 2}
                            parentLabel={parentLabel}
                            showLabel={showInsertLabels}
                        />
                    </div>
                );
            })}
            {blocks.length === 0 && (
                <P
                    noDefaultMargin
                    size="small"
                    styles={{
                        color: 'var(--mark-muted)',
                        padding: '0.4rem 0'
                    }}
                >
                    No blocks yet. Use + to add one.
                </P>
            )}
        </div>
    );
};
const EditableBlockList: React.FC<Props> = (props) => {
    const { canvas } = useEditorContext();
    return canvas ? <CanvasBlockList {...props} /> : <LegacyBlockList {...props} />;
};
export default EditableBlockList;
